import { useCallback, useEffect, useState } from 'react';
import Select from 'react-select';

import './roles.scss';

import { updateRolesBySection } from '~modules/role/api';
import { RoleDto } from '~modules/role/dto';
import { SectionDto } from '~modules/section/model';
import { useLoadSections } from '~pages/cabinet/admin/sections/SectionList/use-load-sections';
import { toastError, toastSuccess } from '~shared/ui';
import { Preloader } from '~shared/ui';
import { SaveBtn } from '~shared/ui/button/save-btn';

import { AddRoleItem } from './add-role-item';
import { RoleItem } from './role-item';
import { useRoles } from './use-roles';

export function RolesPage() {
	const { list, isPending } = useLoadSections();
	const [sectionId, setSectionId] = useState<number | null>(null);
	const { list: roles, pending: isPendingRoles } = useRoles(sectionId);
	const [data, setData] = useState<RoleDto[]>([]);
	const [newRow, setNewRow] = useState<Partial<RoleDto>>({});

	useEffect(() => {
		setData(roles);
	}, [roles]);

	// Выносим обработчики в отдельные функции
	const handleRoleChange = useCallback(
		(userId: number, updates: Partial<RoleDto>) => {
			if (!sectionId) return;

			setData(prevData =>
				prevData.map(r => (r.user_id === userId && r.section_id === sectionId ? { ...r, ...updates } : r)),
			);
		},
		[sectionId],
	);

	const handleRoleDelete = useCallback(
		(userId: number) => {
			if (!sectionId) return;

			setData(prevData => prevData.filter(r => !(r.user_id === userId && r.section_id === sectionId)));
		},
		[sectionId],
	);

	const handleNewRowChange = useCallback((updates: Partial<RoleDto>) => {
		setNewRow(prev => ({ ...prev, ...updates }));
	}, []);

	const handleAddRole = useCallback(() => {
		if (!sectionId || !newRow.user || !newRow.user.id || !newRow.name) {
			if (!newRow.user?.id) toastError('не указан пользователь');
			else if (!newRow.name) toastError('не указана роль');
			return;
		}

		if (data.some(v => v.user_id === newRow.user?.id)) {
			toastError('У этого сотрудника уже есть права');
			return;
		}

		const dto: RoleDto = {
			user_id: newRow.user.id,
			user: newRow.user,
			section_id: sectionId,
			name: newRow.name,
		};

		setData(prev => [...prev, dto]);
		setNewRow({});
	}, [data, newRow, sectionId]);

	const handleCancelAdd = useCallback(() => {
		setNewRow({});
	}, []);

	const handleSave = useCallback(async () => {
		if (sectionId) {
			try {
				await updateRolesBySection(sectionId, data);
				toastSuccess('Настройки ролей обновлены');
			} catch (err) {
				if (err instanceof Error) {
					toastError(err.message);
				} else console.error(err);
			}
		}
	}, [data, sectionId]);

	const handleSectionChange = useCallback((selectedSection: SectionDto | null) => {
		setSectionId(selectedSection?.id ?? null);
	}, []);

	const renderRoleItem = useCallback(
		(role: RoleDto) => (
			<RoleItem
				user={role.user ?? null}
				name={role.name}
				onChange={({ user, name }) => {
					if (user?.id) {
						handleRoleChange(role.user_id, { user, name });
					}
				}}
				onDelete={user => {
					if (user?.id) {
						handleRoleDelete(user.id);
					}
				}}
			/>
		),
		[handleRoleChange, handleRoleDelete],
	);

	return (
		<div className="roles-page page">
			<div className="page-container">
				{isPending ? (
					<Preloader />
				) : (
					<>
						<h2 className="page-title" style={{ marginBottom: '40px' }}>
							Роль на участке
						</h2>
						<div className="section-select">
							<label htmlFor="section-select">Участок</label>
							<Select<SectionDto>
								inputId="section-select"
								className="react-select-container section-input"
								classNamePrefix="react-select"
								getOptionLabel={option => `${option.title}`}
								getOptionValue={option => `${option.id}`}
								options={list}
								isClearable={true}
								onChange={handleSectionChange}
								placeholder="Выберите участок..."
								noOptionsMessage={() => 'Нет доступных участков'}
							/>
						</div>

						{isPendingRoles ? (
							<Preloader />
						) : (
							<table className="table-for-tabel">
								<thead>
									<tr>
										<th>Сотрудник</th>
										<th>Роль</th>
									</tr>
								</thead>
								<tbody>
									{data.map(role => (
										<tr key={role.user_id}>{renderRoleItem(role)}</tr>
									))}

									{sectionId && (
										<AddRoleItem
											user={newRow?.user ?? null}
											name={newRow?.name ?? ''}
											onChange={handleNewRowChange}
											onAccept={handleAddRole}
											onCancel={handleCancelAdd}
										/>
									)}
								</tbody>
							</table>
						)}
						<SaveBtn
							style={{ margin: '20px auto 255px auto' }}
							handleSave={handleSave}
							disabled={!sectionId}
						/>
					</>
				)}
			</div>
		</div>
	);
}
